import { Injectable } from '@angular/core';
import { AngularFirestore, CollectionReference } from '@angular/fire/compat/firestore';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { GameHome } from '../models/game-home.model';

import { HttpService } from './http.service';
import { WagerProduct } from '../models/wager.getinfo.model';
import { isRemoteLogoUrl } from '../utils/game-logo.util';

@Injectable({
  providedIn: 'root',
})
export class GameHomeService {
  private collectionName = 'gamesHome';
  private collectionHeader = 'gamesHeader';
  private collection: CollectionReference<GameHome>;

  constructor(private afs: AngularFirestore, private httpS: HttpService) {
    this.collection = this.afs.collection<GameHome>(this.collectionName).ref;
  }

  /**
   * Obtiene todos los documentos de la colección "gamesHome"
   * y los mapea a tu interfaz GameHome.
   */
  getAllGames(): Observable<GameHome[]> {
    const games$ = this.afs
      .collection<GameHome>(this.collectionName, (ref) => ref.where('isActive', '==', true))
      .valueChanges({ idField: 'id' });

    const progol$ = this.afs.collection('progol').doc('Dx7AxoEEC9MTXN6lD2La').valueChanges();
    const progolMS$ = this.afs.collection('progolMediaSemana').doc('7yhha7HFZmENtURnYtgB').valueChanges();
    const protouch$ = this.afs.collection('protouch').doc('9T0X2FFA5NyGabaMBm6n').valueChanges();

    return combineLatest([games$, progol$, progolMS$, protouch$]).pipe(
      map(([games, progol, progolMS, protouch]) => {
        return games.map((g) => {
          const updated = { ...g };
          if (g.name === 'Progol') {
            const p = progol as any;
            if (p) {
              if (p.closingDraw) updated.close = p.closingDraw;
              if (p.openingDraw) updated.next = p.openingDraw;
            }
          } else if (g.name === 'Progol Media Semana') {
            const pm = progolMS as any;
            if (pm) {
              if (pm.closingDraw) updated.close = pm.closingDraw;
              if (pm.openingDraw) updated.next = pm.openingDraw;
            }
          } else if (g.name === 'ProTouch') {
            const pt = protouch as any;
            if (pt) {
              if (pt.closingDraw) updated.close = pt.closingDraw;
              if (pt.openingDraw) updated.next = pt.openingDraw;
            }
          }
          // Logos de electrónicos subidos desde CMS (gamesHome.logo = URL)
          if (isRemoteLogoUrl(g.logo)) {
            updated.dynamicLogoUrl = g.logo;
          } else if (g.dynamicLogoUrl && isRemoteLogoUrl(g.dynamicLogoUrl)) {
            // keep existing
          }
          return updated;
        });
      })
    );
  }

  getGamesHeader(): Observable<GameHome[]> {
    return this.afs
      .collection<GameHome>(this.collectionHeader, (ref) => ref.where('isAvailable', '==', true))
      .valueChanges({ idField: 'id' });
  }

  /**
   * Ejemplo: Obtiene un solo documento por su ID.
   */
  getGameById(id: string): Observable<GameHome | undefined> {
    return this.afs.collection<GameHome>(this.collectionName).doc(id).valueChanges({ idField: 'id' });
  }

  /**
   * Ejemplo: Crea un nuevo documento en la colección.
   */
  addGame(game: GameHome) {
    return this.afs.collection<GameHome>(this.collectionName).add(game);
  }

  /**
   * Ejemplo: Actualiza un documento existente (por su ID).
   */
  updateGame(gameId: string, data: Partial<GameHome>) {
    return this.afs.collection<GameHome>(this.collectionName).doc(gameId).update(data);
  }

  /**
   * Ejemplo: Elimina un documento de la colección.
   */
  deleteGame(gameId: string) {
    return this.afs.collection<GameHome>(this.collectionName).doc(gameId).delete();
  }

  // Obtener un elemento por product
  async getByProduct(product: WagerProduct | string): Promise<GameHome | null> {
    const snap = await this.collection.where('product', '==', product).limit(1).get();
    if (snap.size) {
      const data = { ...snap.docs[0].data(), id: snap.docs[0].id } as GameHome;

      return data;
    } else {
      return null;
    }
  }
}
